// Small widgets several views share: the grouped data-point picker, the place search, a sortable column header and
// the table pager.
import { api, type Metric, type SearchResults } from "./api.ts";
import { el } from "./dom.ts";
import { LEVEL_LABEL } from "../shared/format.ts";

/**
 * A data-point picker grouped by category (in `categories` order, else as they come). `include` decides which are
 * listed; `unavailable` gives a reason one can't be picked here, shown after its label (the current value stays pickable).
 */
export function metricSelect(o: {
  metrics: Iterable<Metric>;
  value: string;
  onChange: (id: string) => void;
  label?: string;
  cls?: string;
  categories?: readonly string[];
  include?: (m: Metric) => boolean;
  unavailable?: (m: Metric) => string | null;
}): HTMLSelectElement {
  const groups = Map.groupBy([...o.metrics].filter((m) => o.include?.(m) ?? true), (m) => m.category);
  const order = o.categories ? o.categories.filter((c) => groups.has(c)) : [...groups.keys()];
  return el("select", { class: o.cls ?? "metric-select", "aria-label": o.label ?? "Metric", onchange: (e: Event) => o.onChange((e.target as HTMLSelectElement).value) },
    ...order.map((cat) => el("optgroup", { label: cat }, ...groups.get(cat)!.map((m) => {
      const why = o.unavailable?.(m) ?? null;
      return el("option", { value: m.id, selected: m.id === o.value, disabled: why != null && m.id !== o.value }, why ? `${m.label} (${why})` : m.label);
    }))));
}

export type Place = SearchResults["results"][number];

/** A place search box: matches as you type (2+ characters), `keep` filters them, Enter or a click picks one. */
export function placeSearch(o: { placeholder: string; label: string; listClass: string; keep?: (r: Place) => boolean; onPick: (r: Place) => void }) {
  const input = el("input", { type: "search", placeholder: o.placeholder, "aria-label": o.label, autocomplete: "off" });
  const list = el("ul", { class: `search-results ${o.listClass}`, role: "listbox", hidden: true });
  let ab: AbortController | undefined;
  input.addEventListener("input", async () => {
    ab?.abort();
    const q = input.value.trim();
    if (q.length < 2) { list.hidden = true; return; }
    ab = new AbortController();
    try {
      const { results } = await api.search(q, ab.signal);
      const ok = results.filter((r) => o.keep?.(r) ?? true);
      list.replaceChildren(...ok.map((r) => el("li", { role: "option", tabindex: 0, onclick: () => o.onPick(r) },
        el("span", {}, r.level === "zip" ? `${r.code} · ${r.name}` : r.name), el("span", { class: "tag" }, LEVEL_LABEL[r.level]!))));
      list.hidden = ok.length === 0;
    } catch { /* aborted */ }
  });
  input.addEventListener("keydown", (e) => { if (e.key === "Enter") (list.querySelector("li") as HTMLElement | null)?.click(); });
  return { input, list };
}

/** A sortable column header: click or Enter sorts by it (the caller flips the direction when it's already active). */
export function sortHeader(label: string, o: { active: boolean; dir: "asc" | "desc"; num?: boolean; title?: string; onSort: () => void }) {
  return el("th", {
    class: `${o.num ?? true ? "num " : ""}sortable ${o.active ? "active" : ""}`, tabindex: 0, title: `Sort by ${o.title ?? label}`,
    "aria-sort": o.active ? (o.dir === "asc" ? "ascending" : "descending") : "none",
    onclick: o.onSort, onkeydown: (e: Event) => { if ((e as KeyboardEvent).key === "Enter") o.onSort(); },
  }, label, o.active ? (o.dir === "asc" ? " ▲" : " ▼") : "");
}

/** Prev / "Page n of m" / Next under a table; nothing for a single page. */
export function pager(page: number, pages: number, go: (step: -1 | 1) => void) {
  return pages > 1 ? el("footer", { class: "table-foot" },
    el("button", { class: "btn small", disabled: page <= 1, onclick: () => go(-1) }, "‹ Prev"),
    el("span", { class: "muted small" }, `Page ${page} of ${pages}`),
    el("button", { class: "btn small", disabled: page >= pages, onclick: () => go(1) }, "Next ›")) : null;
}
